import { resizeImageToSize } from "@/core/utils/resize-image"
import { useEffect, useState } from "react"
import { formattedPSImagePathToUrl } from "@/store/Pixieset/pixieset-image-urls"
import { PhotoResponseDTO } from "@/api/PSApiOperations"
import { ImageInfoRecord, StyleReferenceRecord } from "@/lib/catalog/types"
import { getReferenceWebpData256x256 } from "@/lib/style/reference-webp-data"
import { photosAtom } from "../photo/photos-state"
import { fetchCurrentPreview } from "@/storage/indexeddb"
import { PreviewSize } from "../previews/previews-types"
import { projectAtom } from "../project/projects-state"
import { store } from "../store"

export const useReferenceImageUrl = (props: {
	reference?: StyleReferenceRecord
	remotePhoto?: PhotoResponseDTO
	localPhotoId?: string
	imageData?: Blob
	fallBackImageInfo?: ImageInfoRecord
}) => {
	const [url, setUrl] = useState<string | null>(null)

	useEffect(() => {
		let fallbackImageInfo = props.fallBackImageInfo
		let localId: string | undefined = props.localPhotoId
		if (props.remotePhoto?.path) {
			setUrl(formattedPSImagePathToUrl(props.remotePhoto?.path_thumb))
		} else if (props.reference) {
			localId = props.reference.imageInfo.imageId
			let afterUrl = props.reference.afterImageUrl
			if (
				!afterUrl &&
				props.reference.afterImageData &&
				props.reference.afterImageData.size > 0
			) {
				afterUrl = URL.createObjectURL(props.reference.afterImageData)
			}
			if (!afterUrl) {
				//Falling back to generate from referenceWebpData256x256 below
				fallbackImageInfo = props.reference.imageInfo
			} else {
				setUrl(afterUrl)
			}
		} else if (props.imageData) {
			setUrl(URL.createObjectURL(props.imageData))
		}

		const projectId = store.get(projectAtom)?.id
		const photos = store.get(photosAtom)
		const photo = localId ? photos[localId] : undefined
		if (!url && projectId && photo) {
			void fetchCurrentPreview({
				projectId: projectId,
				photo: photo,
				size: PreviewSize.small,
			}).then((preview) => {
				if (preview?.data) {
					setUrl(URL.createObjectURL(preview?.data))
				}
			})
		}
		if (!url && fallbackImageInfo) {
			const size = fallbackImageInfo.features.imageSize
			if (!size) {
				setUrl(null)
				return
			}
			void (async () => {
				const imageData = await getReferenceWebpData256x256(fallbackImageInfo)
				if (!imageData) {
					setUrl(null)
					return
				}
				const blob = new Blob([imageData as unknown as BlobPart], {
					type: "image/webp",
				})
				const resized = await resizeImageToSize({
					image: blob,
					options: { outputQuality: 1.0 },
					longestSide: 256,
					originalSize: size,
				})
				setUrl(URL.createObjectURL(resized))
			})().catch(() => {
				setUrl(null)
			})
		}

		return () => {
			if (url) {
				URL.revokeObjectURL(url)
			}
		}
	}, [])

	return url
}
